import { create } from "zustand";
import type { Conflicts, Meta, ReplayChapter, ReplayDef, Stats } from "./data";
import { familyMask, type Warp } from "./replay";

export const MIN_T = -3000;
export const MIN_SPAN = 14 / 365.25; // two weeks
export const MAX_SPAN = 6000;

export interface PickedEvent {
  src: "named" | "ged";
  name: string;
  date: string;
  place?: string;
  deaths?: number | null;
  deathsRange?: [number, number] | null;
  kind: number;
  conflict: number; // index, -1 when none
  lon: number;
  lat: number;
  id?: string; // Wikidata QID for named events (curated events have their own ids and a source url)
  url?: string;
  provisional?: boolean;
  precise?: number; // UCDP location precision 1–7
}

export interface Hover { x: number; y: number; title: string; lines: string[] }

export interface Layers { borders: boolean; areas: boolean; events: boolean; labels: boolean }

export interface FlyTarget { lon?: number; lat?: number; zoom?: number; bbox?: number[]; exact?: boolean; duration?: number }

/** A replay in progress: a war played from start to end at its own pace, pausing at chapters. */
export interface ReplayRun {
  key: string; // curated replay id, or the conflict's QID
  def: ReplayDef | null; // the curated replay, if there is one
  i: number; // the war (conflict index)
  title: string;
  t0: number;
  t1: number;
  seconds: number; // running time at 1×, pauses aside
  warp: Warp;
  chapters: ReplayChapter[];
  hold: number; // seconds left in the current pause (lead-in or chapter)
  tSet: number; // the playhead time the replay last set; any other value means the user moved it
  done: boolean;
}

interface State {
  meta: Meta | null;
  conflicts: Conflicts | null;
  stats: Stats | null;
  error: string | null;
  now: number;
  t: number;
  span: number;
  playing: boolean;
  speed: number; // spans per minute
  selected: number;
  event: PickedEvent | null;
  hover: Hover | null;
  hoverConflict: number;
  layers: Layers;
  kinds: boolean[];
  view: { lon: number; lat: number; zoom: number };
  flyTo: (FlyTarget & { id: number }) | null;
  sheet: "collapsed" | "half" | "full";
  about: boolean;
  replays: ReplayDef[] | null;
  replay: ReplayRun | null;
  focus: Uint8Array | null; // the selected conflict's family: its battles stay bright on the map
  panel: "moment" | "replays";

  setData(meta: Meta, conflicts: Conflicts, stats: Stats): void;
  setError(e: string): void;
  setT(t: number): void;
  setSpan(span: number): void;
  setRange(a: number, b: number): void;
  setPlaying(p: boolean): void;
  setSpeed(s: number): void;
  select(i: number, opts?: { jump?: boolean; fly?: boolean }): void;
  setEvent(e: PickedEvent | null): void;
  setHover(h: Hover | null): void;
  setHoverConflict(i: number): void;
  toggleLayer(k: keyof Layers): void;
  toggleKindGroup(group: number, groups: number[]): void;
  setView(v: { lon: number; lat: number; zoom: number }): void;
  fly(target: FlyTarget): void;
  setSheet(s: State["sheet"]): void;
  setAbout(open: boolean): void;
  setReplays(list: ReplayDef[]): void;
  setPanel(p: State["panel"]): void;
  beginReplay(run: ReplayRun): void;
  patchReplay(patch: Partial<ReplayRun>): void;
  endReplay(): void;
}

const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
let flyId = 0;

export const useApp = create<State>((set, get) => ({
  meta: null,
  conflicts: null,
  stats: null,
  error: null,
  now: 2026.8,
  t: 2026.8,
  span: 240,
  playing: false,
  speed: 1,
  selected: -1,
  event: null,
  hover: null,
  hoverConflict: -1,
  layers: { borders: true, areas: true, events: true, labels: true },
  kinds: [true, true, true, true, true, true, true],
  view: { lon: 20, lat: 25, zoom: 1.6 },
  flyTo: null,
  sheet: "half",
  about: false,
  replays: null,
  replay: null,
  focus: null,
  panel: "moment",

  setData: (meta, conflicts, stats) => set({ meta, conflicts, stats, now: meta.now, t: clamp(get().t, MIN_T, meta.now) }),
  setError: (error) => set({ error }),
  setT: (t) => set({ t: clamp(t, MIN_T, get().now) }),
  setSpan: (span) => set({ span: clamp(span, MIN_SPAN, MAX_SPAN) }),
  setRange: (a, b) => {
    const span = clamp(b - a, MIN_SPAN, MAX_SPAN);
    set({ span, t: clamp((a + b) / 2, MIN_T, get().now) });
  },
  setPlaying: (playing) => set({ playing }),
  setSpeed: (speed) => set({ speed }),
  select: (i, opts = {}) => {
    const c = get().conflicts;
    const r = get().replay;
    if (i < 0 || !c) {
      set({ selected: -1, focus: null, ...(r ? { replay: null, playing: false } : {}) });
      return;
    }
    const patch: Partial<State> = { selected: i, event: null, focus: i === get().selected ? get().focus : familyMask(c, i), panel: "moment" };
    if (r && r.i !== i) Object.assign(patch, { replay: null, playing: false });
    const t0 = c.t0[i], t1 = c.t1[i], { t } = get();
    if (opts.jump && !(t >= t0 && t <= t1)) {
      patch.t = clamp(t0 + Math.min((t1 - t0) * 0.02, 0.05), MIN_T, get().now);
      const span = clamp(Math.max((t1 - t0) * 2.6, 1.5), MIN_SPAN, MAX_SPAN);
      if (span < get().span || get().span > (t1 - t0) * 40) patch.span = span;
    }
    if (opts.fly) {
      const bb = c.bb[i];
      patch.flyTo = bb ? { bbox: bb, id: ++flyId } : c.hasPoint[i] ? { lon: c.lon[i], lat: c.lat[i], zoom: 4, id: ++flyId } : null;
    }
    set(patch);
  },
  setEvent: (event) => set(event ? { event, panel: "moment" } : { event }),
  setHover: (hover) => set({ hover }),
  setHoverConflict: (hoverConflict) => set({ hoverConflict }),
  toggleLayer: (k) => set({ layers: { ...get().layers, [k]: !get().layers[k] } }),
  toggleKindGroup: (group, groups) => {
    const kinds = [...get().kinds];
    const on = !groups.every((g, k) => g !== group || kinds[k]);
    groups.forEach((g, k) => { if (g === group) kinds[k] = on; });
    set({ kinds });
  },
  setView: (view) => set({ view }),
  fly: (target) => set({ flyTo: { ...target, id: ++flyId } }),
  setSheet: (sheet) => set({ sheet }),
  setAbout: (about) => set({ about }),
  setReplays: (replays) => set({ replays }),
  setPanel: (panel) => set({ panel }),
  beginReplay: (run) => {
    const c = get().conflicts!;
    const focus = familyMask(c, run.i);
    for (const j of run.def?.also ?? []) familyMask(c, j).forEach((v, k) => { if (v) focus[k] = 1; });
    set({
      replay: run, selected: run.i, focus, event: null, panel: "moment", playing: true,
      t: clamp(run.t0, MIN_T, get().now), span: clamp((run.t1 - run.t0) * 1.25, MIN_SPAN, MAX_SPAN),
    });
  },
  patchReplay: (patch) => { const r = get().replay; if (r) set({ replay: { ...r, ...patch } }); },
  endReplay: () => set({ replay: null, playing: false }),
}));

/** Visible timeline range: the playhead is the centre, except near today, where the range stops just past
 * today and the playhead moves right instead (no empty future). */
export function visibleRange(t: number, span: number, now = useApp.getState().now): [number, number] {
  const right = now + span * 0.04;
  let b = t + span / 2;
  if (b > right) b = Math.max(right, t + span * 0.02);
  return [b - span, b];
}
